import React from "react";
import { YardDateField } from "../../../components/common/YardDateField";
import { useHistory } from "react-router-dom";

import { Grid, Typography, useMediaQuery } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import {
  ArrayField,
  BooleanField,
  Datagrid,
  FunctionField,
  Labeled,
  NumberField,
  ReferenceField,
  SelectField,
  Show,
  Tab,
  TabbedShowLayout,
  TextField,
  TopToolbar,
  useShowController,
} from "react-admin";

import { BackButton } from "../../../components/common/BackButton";
import {
  CustomField,
  CustomFieldsMobile,
} from "../../../components/common/CustomFields";
import { FileField } from "../../../components/common/FileField";
import { ForceTitle } from "../../../components/common/ForceTitle";
import { ShowButton } from "../../../components/common/ShowButton";
import CustomizableDatagrid from "../../../components/common/ra-customizable-datagrid";
import { SendPOSDialogButton } from "../../../components/common/SendPOSDialogButton";
import { NotesTabShow } from "../../../components/common/NotesTab";

import {
  AclType,
  CarrierType,
  CarrierTypes,
  InboundStatus,
  LotTypes,
} from "../../../utils/constants";
import {
  deepRedirect,
  getBackPath,
  quantityRenderer,
} from "../../../utils/utils";
import { isAuthorized } from "../../../utils/appUtils";

import {
  calcTotalQuantity,
  getPODEmailSubject,
  handleSendPOD,
  renderTotalNetWeight,
} from "../../../managerApp/resources/inboundShipments/InboundUtils";
import { InboundAside } from "./InboundAside";

const useStyles = makeStyles({
  alignRight: {
    textAlign: "right",
  },
});

const QuantityWithUnitsRenderer = ({ record, source, units }) => {
  return (
    <div style={{ display: "flex", justifyContent: "flex-end" }}>
      <NumberField record={record} source={source} />
      &nbsp;{record[units]}
    </div>
  );
};

const ShipmentSetupTab = ({ gteMedium, ...props }) => {
  const { record } = props;

  return (
    <Grid container direction="column" spacing={1}>
      <Grid container direction="row" spacing={1}>
        <Grid item xs={12} md={6}>
          <Labeled label="Origin">
            <TextField source="shipFrom" />
          </Labeled>
        </Grid>
      </Grid>
      <Grid container item direction="row" spacing={1}>
        <Grid item xs={12} md={6}>
          <Labeled label="Load Type">
            <ReferenceField
              {...props}
              source="reloadYardCommodityId"
              reference="reloadYardCommoditys"
              link={false}
            >
              <TextField source="commodity.name" />
            </ReferenceField>
          </Labeled>
        </Grid>
        <Grid item xs={12} md={6}>
          <Labeled label="Carrier Type">
            <SelectField source="carrierType" choices={CarrierTypes} />
          </Labeled>
        </Grid>
        <Grid item xs={12} md={6}>
          <Labeled label="Carrier Name">
            <TextField source="carrierName" />
          </Labeled>
        </Grid>
        <Grid item xs={12} md={6}>
          <Labeled
            label={
              record.carrierType === CarrierType.RAIL
                ? "Railcar Number"
                : "Trailer Number"
            }
          >
            <TextField source="railCarNumber" />
          </Labeled>
        </Grid>
      </Grid>
      <Grid container direction="row" spacing={1}>
        <Grid item xs={12} md={6}>
          <Labeled label="PO Number">
            <TextField source="poNumber" />
          </Labeled>
        </Grid>
      </Grid>
      <Grid container direction="row" spacing={1}>
        <Grid item xs={12} md={6}>
          <Labeled label="Default Area">
            <ReferenceField
              {...props}
              source="areaId"
              reference="areas"
              link={false}
            >
              <TextField source="name" />
            </ReferenceField>
          </Labeled>
        </Grid>
        <Grid item xs={12} md={6}>
          <Labeled label="Default Zone">
            <ReferenceField
              {...props}
              source="zoneId"
              reference="zones"
              link={false}
            >
              <TextField source="name" />
            </ReferenceField>
          </Labeled>
        </Grid>
      </Grid>
      <Grid container direction="row" spacing={1}>
        <Grid item xs={12} md={6}>
          <Labeled label="Lot Type">
            <SelectField source="lotType" choices={LotTypes} />
          </Labeled>
        </Grid>
        <Grid item xs={12} md={6}>
          <Labeled label="Lot Number">
            <TextField source="lotNumber" />
          </Labeled>
        </Grid>
      </Grid>
      <Grid container direction="row" spacing={1}>
        <Grid item xs={12}>
          <Labeled label="Special Instructions">
            <TextField source="instructions" multiline />
          </Labeled>
        </Grid>
      </Grid>
      {record.customFields && (
        <CustomFieldsMobile
          recordFields={record.customFields}
          accountFields={record.account?.inboundCustomFields}
          width={6}
        />
      )}
      {record?.account?.trackInboundSeals && (
        <Grid container xs={12} md={6}>
          <Labeled label="Seals" fullWidth>
            <ArrayField source="seals.items">
              <Datagrid>
                <BooleanField source="validated" />
                <TextField source="barcode" />
                <TextField source="discribtion" />
              </Datagrid>
            </ArrayField>
          </Labeled>
        </Grid>
      )}
      {gteMedium && (
        <Grid container direction="row" spacing={1}>
          <Grid item xs={6}>
            <Labeled label="ATTACHMENTS">
              <FileField
                source="attachments"
                src="src"
                title="title"
                target="_blank"
              />
            </Labeled>
          </Grid>
        </Grid>
      )}
      <Grid container>
        <Grid item xs={12}></Grid> &nbsp;{" "}
      </Grid>
    </Grid>
  );
};

const TallySheetTab = (props) => {
  const classes = useStyles();
  const { history, backPath, record } = props;
  const account = record.account;

  const accountFields =
    account?.inventoryCustomFields?.length > 0
      ? account.inventoryCustomFields
      : [];
  const customColumns = [];
  const customGridFields = accountFields
    ? accountFields.map((field, index) => {
        customColumns.push(field.name);
        return (
          <CustomField
            source={`customFields[${index}]`}
            field={field}
            label={field.name}
            sortable={true}
          />
        );
      })
    : [];

  const openInventory = (jsonRecord) => {
    const jsonObject = JSON.parse(jsonRecord);
    const toPath = `/inventories/${jsonObject.id}/show`;
    deepRedirect(history, toPath, backPath, {
      inboundRecord: record,
    });
  };

  return (
    <Grid container direction="column" spacing={1}>
      <Grid item xs={12}>
        <ArrayField
          source="inventories.items"
          basePath="/inventories"
          fullWidth
        >
          <CustomizableDatagrid
            label="INBOUND INVENTORY"
            hasBulkActions={false}
            rowClick={openInventory}
            storageProp={`InboundShoww_${account?.id}`}
            defaultColumns={[
              "productName",
              "quantityAdvised",
              "quantityReceived",
              ...customColumns,
            ]}
          >
            {[
              <TextField source="productName" label="Product Name" />,
              <FunctionField
                label="Qty. Advised"
                render={(record) => (
                  <QuantityWithUnitsRenderer
                    record={record}
                    source="quantityAdvised"
                    units="inventoryUOM"
                  />
                )}
                headerClassName={classes.alignRight}
                cellClassName={classes.alignRight}
              />,
              <FunctionField
                label="Qty. Received"
                render={(record) => (
                  <QuantityWithUnitsRenderer
                    record={record}
                    source="quantityReceived"
                    units="inventoryUOM"
                  />
                )}
                headerClassName={classes.alignRight}
                cellClassName={classes.alignRight}
              />,
              ...customGridFields,
              <ShowButton {...props} state={{ inboundRecord: record }} />,
            ]}
          </CustomizableDatagrid>
        </ArrayField>
      </Grid>
      <Grid item xs={12}>
        <Typography variant="overline" color="textSecondary">
          TOTAL LOAD
        </Typography>
        <Grid container direction="row" spacing={1}>
          <Grid item xs={6} md={3}>
            <Labeled label="Total Quantity">
              <FunctionField
                render={(record) =>
                  quantityRenderer(calcTotalQuantity(record), record.totalUOM)
                }
              />
            </Labeled>
          </Grid>

          <Grid item xs={6} md={3}>
            <Labeled label="Total Weight">
              <FunctionField
                render={(record) => renderTotalNetWeight(record)}
              />
            </Labeled>
          </Grid>
        </Grid>
      </Grid>

      {!record?.skipInvoice && isAuthorized(AclType.INVOICES_VIEW) && (
        <Grid item xs={12}>
          <fieldset>
            <legend>
              <Typography variant="overline" color="textSecondary">
                Billing
              </Typography>
            </legend>
            <Grid item container xs={12} spacing={6}>
              <Grid item xs={12} md={6}>
                <Labeled label="Manual Charges" fullWidth>
                  <ArrayField source="manualCharges.items" fullWidth>
                    <Datagrid bulkActionButtons={false}>
                      <FunctionField
                        label="Service"
                        render={(mc) => mc?.chargeRule?.service?.name || ""}
                      />
                      <NumberField source="quantity" label="Quantity" />
                      <TextField source="chargeRule.billingUOM" label="UOM" />
                    </Datagrid>
                  </ArrayField>
                </Labeled>
              </Grid>
            </Grid>
          </fieldset>
        </Grid>
      )}
    </Grid>
  );
};

const grossRender = (record) => {
  if (
    record.tareWeight !== null &&
    record.tareWeight >= 0 &&
    record.ladenWeight >= 0
  ) {
    return `${(record.ladenWeight - record.tareWeight).toLocaleString()} ${
      record.weightUnits ? record.weightUnits : record.scaleWeightUnits
    }`;
  } else
    return `0 ${
      record.weightUnits ? record.weightUnits : record.scaleWeightUnits
    }`;
};

const ShipmentTraceTab = (props) => {
  const { record } = props;

  return (
    <Grid container direction="column" spacing={1}>
      <Grid item xs={12} md={4}>
        <Labeled label="Time Scheduled">
          <YardDateField source="scheduledDateTime" showTime />
        </Labeled>
      </Grid>
      <Grid container direction="row" spacing={1}>
        <Grid item xs={12} md={4}>
          <Labeled label="Time Notified">
            <YardDateField source="notifyDateTime" showTime />
          </Labeled>
        </Grid>
        {record.carrierType === CarrierType.TRUCK && (
          <Grid item xs={12} md={4}>
            <Labeled label="Carrier Name">
              <TextField source="carrierName" />
            </Labeled>
          </Grid>
        )}
        {record.carrierType === CarrierType.TRUCK && (
          <Grid item xs={12} md={4}>
            <Labeled label="Trailer Number">
              <TextField source="railCarNumber" />
            </Labeled>
          </Grid>
        )}
      </Grid>
      <Grid container direction="row" spacing={1}>
        <Grid item xs={12} md={4}>
          <Labeled label="Time Placed">
            <YardDateField source="placedDateTime" showTime />
          </Labeled>
        </Grid>
        {record.carrierType === CarrierType.TRUCK && (
          <>
            <Grid item xs={12} md={4}>
              <Labeled label="Laden Weight">
                <NumberField source="ladenWeight" />
              </Labeled>
            </Grid>
            <Grid item xs={12} md={4}>
              <Labeled label="Units">
                <TextField source="scaleWeightUnits" />
              </Labeled>
            </Grid>
          </>
        )}
      </Grid>
      <Grid item xs={12} md={4}>
        <Labeled label="Unload Start Time">
          <YardDateField source="startDateTime" showTime />
        </Labeled>
      </Grid>
      <Grid item xs={12} md={4}>
        <Labeled label="Unload End Time">
          <YardDateField source="endDateTime" showTime />
        </Labeled>
      </Grid>
      <Grid container direction="row" spacing={1}>
        <Grid item xs={12} md={4}>
          <Labeled label="Time Released">
            <YardDateField source="releaseDateTime" showTime />
          </Labeled>
        </Grid>
        {record.carrierType === CarrierType.TRUCK && (
          <>
            <Grid item xs={12} md={2}>
              <Labeled label="Tare Weight">
                <NumberField source="tareWeight" />
              </Labeled>
            </Grid>
            <Grid item xs={12} md={2}>
              <Labeled label="Gross Weight">
                <FunctionField render={grossRender} />
              </Labeled>
            </Grid>
            <Grid item xs={12} md={2}>
              <Labeled label="Net Weight">
                <NumberField source="netWeight" />
              </Labeled>
            </Grid>
          </>
        )}
      </Grid>
      {record.carrierType === CarrierType.RAIL && (
        <Grid container direction="row" spacing={1}>
          <Grid item xs={12} md={4}>
            <Labeled label="Time Collected">
              <YardDateField source="collectedDateTime" showTime />
            </Labeled>
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

const ShowActions = ({ data, ...props }) => (
  <TopToolbar>
    {data && !data.status?.startsWith(InboundStatus.ADVICE_ALL) && (
      <SendPOSDialogButton
        label="SEND"
        title="Send POD"
        record={data}
        email={data.account?.podEmail}
        subjectDefault={getPODEmailSubject(data)}
        enclosePhotosDefault={data.account?.podPhotos}
        encloseDocsDefault={data.account?.podAttachments}
        handleSend={handleSendPOD}
      />
    )}
    <BackButton {...props} />
  </TopToolbar>
);

export const InboundShow = (props) => {
  const gteMedium = useMediaQuery((theme) => theme.breakpoints.up("md"));
  const { record } = useShowController(props);
  const backPath = getBackPath(props);
  const history = useHistory();
  const exceptions = !record
    ? {}
    : {
        hasException: record.hasException,
        unsafeShipment: record.unsafeShipment,
        unsafeShipmentNote: record.unsafeShipmentNote,
        brokenSeal: record.brokenSeal,
        brokenSealNote: record.brokenSealNote,
        otherException: record.otherException,
        otherExceptionNote: record.otherExceptionNote,
      };

  return (
    <Show
      {...props}
      title={<ForceTitle title="View Inbound" />}
      actions={<ShowActions {...props} backPath={backPath} />}
      aside={
        <InboundAside
          {...props}
          history={history}
          backPath={backPath}
          exceptions={exceptions}
        />
      }
    >
      <TabbedShowLayout>
        <Tab label="Inbound Setup">
          <ShipmentSetupTab {...props} gteMedium={gteMedium} />
        </Tab>
        <Tab label="Tally Sheet">
          <TallySheetTab {...props} history={history} backPath={backPath} />
        </Tab>
        <Tab label="Dispatch">
          <ShipmentTraceTab />
        </Tab>
        <Tab label="Notes">
          <NotesTabShow {...props} hidePrivate={true} />
        </Tab>
      </TabbedShowLayout>
    </Show>
  );
};
